import { Injectable } from '@angular/core';
import {
  InAppBrowser,
  InAppBrowserOptions,
} from '@awesome-cordova-plugins/in-app-browser/ngx';
import { Capacitor, CapacitorHttp, HttpResponseType } from '@capacitor/core';
import { LoadingController, ToastController } from '@ionic/angular';
import { SharedService } from '../shared/shared.service';
import { TranslateService } from '@ngx-translate/core';
import { UserDatum } from '@app/shared/interfaces/files.interface';
import { Router } from '@angular/router';
import { environment } from '@env';

declare var google: any;
declare var gapi: any;
const inAppOptions: InAppBrowserOptions = {
  location: 'no',
  hidden: 'no',
  clearcache: 'no',
  clearsessioncache: 'no',
  zoom: 'no', //Android only
  hardwareback: 'no',
  mediaPlaybackRequiresUserAction: 'no',
  shouldPauseOnSuspend: 'no', //Android only
  closebuttoncolor: '#ffffff',
  disallowoverscroll: 'no', //iOS only
  toolbar: 'no', //iOS only
  enableViewportScale: 'no', //iOS only
  allowInlineMediaPlayback: 'no', //iOS only
  presentationstyle: 'fullscreen', //iOS only
  fullscreen: 'yes', //Windows only,
  hideurlbar: 'yes',
  usewkwebview: 'yes',
  toolbarcolor: '#a9a9a9',
  toolbartranslucent: 'no',
  toolbarposition: 'top',
  hidenavigationbuttons: 'yes',
  closebuttoncaption: 'X',
  lefttoright: 'no',
  hidespinner: 'yes',
};

@Injectable({
  providedIn: 'root',
})
export class DrivePickerService {
  accessToken!: string;
  picker: any;
  constructor(
    public iab: InAppBrowser,
    private toast: ToastController,
    private sharedService: SharedService,
    private translateService: TranslateService,
    private loadingCtrl: LoadingController,
    private router: Router
  ) {}

  createPicker(accessToken){
    this.sharedService.reset();
    this.accessToken = accessToken;
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
      this.createPickerIOS();
    } else {
      this.createPickerWeb();
    }
  }

  pickerInit() {
    gapi.load('picker');
  }

  createPickerIOS() {
    let scriptString = `
      document.querySelector('#gb-main').style.display='none';
      document.querySelectorAll('.fbar')?.forEach((el)=> el.style.display='none');
      var s = document.createElement( 'script' );
      s.setAttribute( 'src', 'https://apis.google.com/js/api.js' );
      s.onload = onApiLoad();
      s.async = true;
      s.defer = true;
      document.body.appendChild( s );
      function onApiLoad() {
        gapi.load('picker', {'callback': onPickerApiLoad});
      }
    
      function onPickerApiLoad() {
          var picker = new google.picker.PickerBuilder()
              .addView(google.picker.ViewId.DOCS)
              .setOAuthToken('${this.accessToken}')
              .setDeveloperKey('${environment.GAPI_API_KEY}')
              .setCallback(pickerCallback)
              .enableFeature(google.picker.Feature.NAV_HIDDEN)
              .enableFeature(google.picker.Feature.FULLSCREEN)
              .setAppId('${environment.GAPI_APP_ID}')
              .build();
          picker.setVisible(true);
          var el = document.getElementsByClassName('picker-dialog-bg')[1];
          if(el){
            el.addEventListener('click', (event)=>{
              var message = JSON.stringify({event: 'close'});
              webkit.messageHandlers.cordova_iab.postMessage(message);
            })
          }
      }
    
      function pickerCallback(data) {
          var url = 'nothing';
          if (data.action == google.picker.Action.PICKED) {
              var doc = data.docs[0];
              url = doc.url;
              var data = {
                url: url, 
                fileId: doc.id, 
                fileName: doc.name, 
                mimeType: doc.mimeType, 
                size: doc.sizeBytes 
              }
              var message = JSON.stringify(data);
              webkit.messageHandlers.cordova_iab.postMessage(message);
          } else if (data.action == google.picker.Action.CANCEL) {
              var message = JSON.stringify({event: 'close'});
            webkit.messageHandlers.cordova_iab.postMessage(message);
          }
      }
    `;

    const browser = this.iab.create('https://www.google.com', '', inAppOptions);

    browser.on('loadstop').subscribe(() => {
      browser.executeScript({ code: scriptString });
      browser.insertCSS({file: "../../theme/style.css"});
    });

    browser.on('loaderror').subscribe((error) => {
      console.log('loaderror', error);
    });
    
    browser.on('message').subscribe((msg) => {
      if (msg?.data?.fileId) {
        const file: UserDatum = {
          id: msg.data.fileId,
          name: msg.data.fileName || '',
          mimeType: msg.data.mimeType || '',
          size: msg.data.size || 0
        }
        this.downloadFile(file);
      }
      browser.close();
    });
  }

  createPickerWeb() {
    this.picker = new google.picker.PickerBuilder()
      .enableFeature(google.picker.Feature.NAV_HIDDEN)
      // .enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
      .setDeveloperKey(environment.GAPI_API_KEY)
      .setAppId(environment.GAPI_APP_ID)
      .setOAuthToken(this.accessToken)
      .addView(google.picker.ViewId.DOCS)
      .setCallback(this.pickerCallback.bind(this))
      .build();
    this.picker.setVisible(true);

    const el = document.getElementsByClassName('picker-dialog-bg')[1];
    if(el){
      el.addEventListener('click', ()=>{
        this.disposePicker();
      })
    }
  }

  disposePicker(){
    if(this.picker){
      this.picker.dispose();
      this.picker = null;
    }
  }

  async pickerCallback(data: any) {
    if (data.action === google.picker.Action.PICKED) {
      const document: any = data[google.picker.Response.DOCUMENTS][0];
      if(document){
        const file: UserDatum = {
          id: document.id,
          name: document.name,
          mimeType: document.mimeType,
          size: document.sizeBytes
        }
        this.downloadFile(file);
      }
    } else if (data.action == google.picker.Action.CANCEL) {
      this.disposePicker();
    }
  }

  async presentToast(message, color) {
    const toast = await this.toast.create({
      message,
      duration: 2000,
      color,
    });
    toast.present();
  }

  async downloadFile(file: UserDatum) {

    if (!this.sharedService.isFileSupportedWithType(file.name, file.mimeType)) {
      this.presentToast(
        this.translateService.instant("FILE_TYPE_NOT_SUPPORTED"),
        "danger"
      );
      return;
    }

    const loading = await this.loadingCtrl.create({
      message: this.translateService.instant("DOWNLOADING_FILES"),
    });

    loading.present();

    // const url =
    // 'https://www.googleapis.com/drive/v3/files/' + fileId + '/export&mimeType=' + mimeType;
    const url =
      'https://www.googleapis.com/drive/v3/files/' + file.id + '?alt=media';
    const respType: HttpResponseType = 'blob';
    const options = {
      url,
      headers: { Authorization: 'Bearer ' + this.accessToken },
      responseType: respType,
    };
    try {
      const response = await CapacitorHttp.get(options);

      if(response?.data){
        await this.sharedService.addBlobToQueue(
          {
            content: response.data,
            contentType: file.mimeType,
            extension: this.sharedService.getExtension(file.name),
            fileName: file.name,
            lastModified: "",
            size: Number(file.size),
          },
          response.data
        );
      }
       loading.dismiss();
      if (!this.sharedService.isDomainReady()) {
        this.router.navigate(["/app/add-files/maps"], {
          state: { showMenu: false },
        });
      } else {
        this.router.navigate(["app/add-files/upload"]);
      }
    } catch (error) {
     // const errorMsg = error.error.message
     this.presentToast(
        this.translateService.instant("FILE_TYPE_NOT_SUPPORTED"),
        "danger"
      );
      console.log('api call error ===>', error);
    } finally {
      loading.dismiss();
    }
  }
}
